import type { QueryClient } from "react-query";

import { PROFILE_INFO_QUERY_KEY } from "@/react/hooks/profile/useProfileInfo";

import { CONNECTION_REQUESTS_QUERY_KEY } from "./useConnectionRequestsInfiniteQuery";

export interface InvalidateConnectionRequestsQueryCache {
  initiatorPublicUid?: string;
  queryClient: QueryClient;
  recipientPublicUid?: string | null;
}

/**
 * Invalidates connection requests query cache.
 *
 * For updatePendingRequestsCount events, only invalidates the connection requests query.
 * For connectionStatusChanged events, also invalidates member profile queries and member list queries
 * to ensure the UI reflects connection status changes across all components.
 *
 * @param queryClient - React Query client instance
 * @param initiatorPublicUid - Optional public UID of the connection initiator (for status change events)
 * @param recipientPublicUid - Optional public UID of the connection recipient (for status change events)
 */
export const invalidateConnectionRequestsQueryCache = ({
  queryClient,
  initiatorPublicUid,
  recipientPublicUid,
}: InvalidateConnectionRequestsQueryCache) => {
  void queryClient.invalidateQueries({
    queryKey: [CONNECTION_REQUESTS_QUERY_KEY],
  });

  if (initiatorPublicUid || recipientPublicUid) {
    void queryClient.invalidateQueries({ queryKey: ["community-members"] });
    void queryClient.invalidateQueries({ queryKey: ["audience"] });

    if (initiatorPublicUid) {
      void queryClient.invalidateQueries({
        queryKey: [PROFILE_INFO_QUERY_KEY, initiatorPublicUid],
      });
    }

    if (recipientPublicUid) {
      void queryClient.invalidateQueries({
        queryKey: [PROFILE_INFO_QUERY_KEY, recipientPublicUid],
      });
    }
  }
};
